import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, MapPin, ChevronRight, ChevronLeft, Loader2, LocateFixed } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LeafletMap } from "@/components/LeafletMap";
import { GeoSearch } from "@/components/GeoSearch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["About you", "Your geo point", "Preferences"];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: user?.name || "", occupation: "", employer: "", photo_url: "",
    latitude: null, longitude: null, location_name: "",
    pref_gender: "", looking_for: "", about: "", partner_prefs: "",
    age: 28, match_age_min: 25, match_age_max: 40,
    likes: "", dislikes: "",
  });

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", data.photo_url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => { set("latitude", pos.coords.latitude); set("longitude", pos.coords.longitude); toast.success("Location captured"); },
      () => toast.error("Could not get your location — tap the map instead")
    );
  };

  const next = () => {
    if (step === 0 && (!f.name || !f.occupation || !f.employer || !f.photo_url))
      return toast.error("Please complete all fields and upload a photo");
    if (step === 1 && (f.latitude == null || f.longitude == null)) return toast.error("Pick your point on the map");
    if (step === 2) {
      if (!f.pref_gender) return toast.error("Tell us who you're interested in");
      if (!f.looking_for) return toast.error("Select what you're looking for");
      if (!f.about.trim()) return toast.error("Tell us about yourself");
      if (!f.likes.trim()) return toast.error("Tell us what you like doing in your neighborhood");
      if (!f.partner_prefs.trim()) return toast.error("Tell us what you're looking for in a partner");
      if (!f.dislikes.trim()) return toast.error("Add at least one dislike");
    }
    if (step < 2) setStep(step + 1);
    else finish();
  };

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        onboarding_complete: true,
      };
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success("Profile complete!");
      navigate("/radar");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen sunny-bg" data-testid="onboarding-page">
     <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-center gap-3 mb-10">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-3 flex-1">
            <div className={`flex-1 h-1 rounded-full transition-colors ${i <= step ? "bg-[#F43F5E]" : "bg-stone-200"}`} />
          </div>
        ))}
      </div>
      <div className="text-[#F43F5E] text-sm font-semibold uppercase tracking-widest">Step {step + 1} / 3</div>
      <h1 className="font-display font-extrabold text-4xl tracking-tight mt-2 mb-8">{STEPS[step]}</h1>

      <div key={step} className="fade-up">
        {step === 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-amber-50 border border-stone-200 flex items-center justify-center">
                {f.photo_url ? <img src={mediaUrl(f.photo_url)} alt="" className="w-full h-full object-cover" />
                  : <Upload className="text-stone-400" />}
              </div>
              <label className="cursor-pointer">
                <span data-testid="upload-photo-label" className="px-4 py-2.5 rounded-full border border-stone-300 hover:border-[#F43F5E] transition-colors text-sm inline-flex items-center gap-2">
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                  {f.photo_url ? "Change photo" : "Upload photo"}
                </span>
                <input data-testid="photo-input" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </label>
            </div>
            <div>
              <Label className="text-stone-600">Name</Label>
              <Input data-testid="onb-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white border-stone-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-600">Occupation</Label>
                <Input data-testid="onb-occupation" value={f.occupation} onChange={(e) => set("occupation", e.target.value)} className="mt-1.5 bg-white border-stone-200" placeholder="Architect" />
              </div>
              <div>
                <Label className="text-stone-600">Employer</Label>
                <Input data-testid="onb-employer" value={f.employer} onChange={(e) => set("employer", e.target.value)} className="mt-1.5 bg-white border-stone-200" placeholder="Studio X" />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-stone-500 text-sm flex items-center gap-2"><MapPin size={16} /> Tap the map to drop your point.</p>
              <Button data-testid="use-location-btn" onClick={useMyLocation} variant="outline" className="border-stone-300 gap-2 rounded-full">
                <LocateFixed size={16} /> Use my location
              </Button>
            </div>
            <GeoSearch onPick={(la, ln, name) => { set("latitude", la); set("longitude", ln); if (name && !f.location_name) set("location_name", name); }} />
            <div className="h-[360px]">
              <LeafletMap lat={f.latitude} lng={f.longitude} onPick={(la, ln) => { set("latitude", la); set("longitude", ln); }} />
            </div>
            {f.latitude != null && (
              <div data-testid="coords-display" className="text-sm text-stone-500 font-mono">
                {f.latitude.toFixed(5)}, {f.longitude.toFixed(5)}
              </div>
            )}
            <div>
              <Label className="text-stone-600">Location name (optional)</Label>
              <Input data-testid="onb-location-name" value={f.location_name} onChange={(e) => set("location_name", e.target.value)} className="mt-1.5 bg-white border-stone-200" placeholder="Brooklyn, NY" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-600">I'm interested in</Label>
                <Select value={f.pref_gender} onValueChange={(v) => set("pref_gender", v)}>
                  <SelectTrigger data-testid="pref-gender" className="mt-1.5 bg-white border-stone-200"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="women">Women</SelectItem>
                    <SelectItem value="men">Men</SelectItem>
                    <SelectItem value="everyone">Everyone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-600">Looking for</Label>
                <Select value={f.looking_for} onValueChange={(v) => set("looking_for", v)}>
                  <SelectTrigger data-testid="pref-looking-for" className="mt-1.5 bg-white border-stone-200"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="love">Love</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-stone-600">Tell us about yourself, what you do, where you're from and something more!</Label>
              <textarea data-testid="onb-about" value={f.about} onChange={(e) => set("about", e.target.value)} rows={3}
                className="mt-1.5 w-full rounded-md bg-white border border-stone-200 focus:border-[#F43F5E] focus:outline-none px-3 py-2 text-sm text-stone-800"
                placeholder="I'm a designer from Chicago who loves rooftop sunsets…" />
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-5 shadow-sm space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-stone-600">How old are you?</Label>
                  <span data-testid="age-value" className="text-lg font-display font-bold text-[#F43F5E]">{f.age}</span>
                </div>
                <Slider data-testid="age-slider" className="mt-4" min={18} max={99} step={1}
                  value={[f.age]} onValueChange={(v) => set("age", v[0])} />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-stone-600">Age range you'd like to be matched with</Label>
                  <span data-testid="age-range-value" className="text-lg font-display font-bold text-[#F43F5E]">{f.match_age_min} – {f.match_age_max}</span>
                </div>
                <Slider data-testid="age-range-slider" className="mt-4" min={18} max={99} step={1} minStepsBetweenThumbs={1}
                  value={[f.match_age_min, f.match_age_max]}
                  onValueChange={(v) => setF((p) => ({ ...p, match_age_min: v[0], match_age_max: v[1] }))} />
                <div className="flex justify-between text-xs text-stone-400 mt-2"><span>18</span><span>99</span></div>
              </div>
            </div>
            <div>
              <Label className="text-stone-600">What do you like doing in your neighborhood? <span className="text-stone-400">(eg. going for walks or runs, doing laundry, buying groceries, exploring farmers markets, star gazing?)</span></Label>
              <textarea data-testid="onb-likes" value={f.likes} onChange={(e) => set("likes", e.target.value)} rows={3}
                className="mt-1.5 w-full rounded-md bg-white border border-stone-200 focus:border-[#F43F5E] focus:outline-none px-3 py-2 text-sm text-stone-800"
                placeholder="Morning runs along the river, farmers markets on Sundays…" />
            </div>
            <div>
              <Label className="text-stone-600">What are some things you're looking for in a partner? <span className="text-stone-400">(eg. Interested in sports, faith, reading, music?)</span></Label>
              <textarea data-testid="onb-partner-prefs" value={f.partner_prefs} onChange={(e) => set("partner_prefs", e.target.value)} rows={3}
                className="mt-1.5 w-full rounded-md bg-white border border-stone-200 focus:border-[#F43F5E] focus:outline-none px-3 py-2 text-sm text-stone-800"
                placeholder="Someone active, into live music, and shares my faith…" />
            </div>
            <div>
              <Label className="text-stone-600">Any dislikes? <span className="text-stone-400">(things that are a no for you)</span></Label>
              <textarea data-testid="onb-dislikes" value={f.dislikes} onChange={(e) => set("dislikes", e.target.value)} rows={2}
                className="mt-1.5 w-full rounded-md bg-white border border-stone-200 focus:border-[#F43F5E] focus:outline-none px-3 py-2 text-sm text-stone-800"
                placeholder="Smoking, loud bars…" />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-10">
        <Button data-testid="onb-back-btn" variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="gap-2 text-stone-500">
          <ChevronLeft size={16} /> Back
        </Button>
        <Button data-testid="onb-next-btn" onClick={next} disabled={saving}
          className="bg-[#F43F5E] hover:bg-[#E11D48] text-white rounded-full px-6 gap-2">
          {saving ? <Loader2 className="animate-spin" size={16} /> : null}
          {step < 2 ? "Continue" : "Let's get coordinated"} <ChevronRight size={16} />
        </Button>
      </div>
     </div>
    </div>
  );
}
